/**
 * Class to handle health and safety-related operations.
 * Provides methods to interact with various health and safety resources via API.
 */
class HealthSafety {
  /**
   * Initializes the HealthSafety class.
   *
   * @param {Request} request - An instance of the Request class to handle HTTP requests.
   */
  constructor(request) {
    this.request = request;
  }

  /**
   * Fetches a specific health and safety report by ID.
   *
   * @param {Number} reportId - The unique identifier of the report.
   * @returns {Promise<Object>} Resolves with the report data.
   */
  async getReport(reportId) {
    return await this.request.request(`health-safety/reports/${reportId}`);
  }

  /**
   * Fetches all health and safety reports.
   *
   * @returns {Promise<Array>} Resolves with an array of reports.
   */
  async getReports() {
    return await this.request.request(`health-safety/reports`);
  }

  /**
   * Fetches all identified hazards.
   *
   * @returns {Promise<Array>} Resolves with an array of hazards.
   */
  async getHazards() {
    return await this.request.request(`health-safety/hazards`);
  }

  /**
   * Fetches all employees associated with health and safety.
   *
   * @returns {Promise<Array>} Resolves with an array of employees.
   */
  async getEmployees() {
    return await this.request.request(`health-safety/employees`);
  }

  /**
   * Fetches all types of equipment used in health and safety reports.
   *
   * @returns {Promise<Array>} Resolves with an array of equipment types.
   */
  async getEquipmentTypes() {
    return await this.request.request(`health-safety/equipment-types`);
  }

  /**
   * Fetches all types of materials used in health and safety reports.
   *
   * @returns {Promise<Array>} Resolves with an array of material types.
   */
  async getMaterialTypes() {
    return await this.request.request(`health-safety/material-types`);
  }

  /**
   * Submits a new health and safety report.
   *
   * @param {Object} data - The data for the new report.
   * @returns {Promise<Object>} Resolves with the created report data.
   */
  async postReport(data) {
    return await this.request.request(`health-safety/reports`, {
      method: "POST",
      data: data,
    });
  }

  /**
   * Updates an existing health and safety report.
   *
   * @param {Number} reportId - The unique identifier of the report.
   * @param {Object} data - The updated data for the report.
   * @returns {Promise<Object>} Resolves with the updated report data.
   */
  async patchReport(reportId, data) {
    return await this.request.request(`health-safety/reports/${reportId}`, {
      method: "PATCH",
      data: data,
    });
  }

  /**
   * Uploads a file to an existing health and safety report.
   *
   * @param {Number} reportId - The unique identifier of the report.
   * @param {File} file - The updated file for the report.
   * @returns {Promise<Object>} Resolves with the updated report data.
   */
  async postReportFile(reportId, file) {
    return await this.request.uploadFile(`health-safety/reports/${reportId}/upload-file`, file);
  }

  /**
   * Deletes a health and safety report by ID.
   *
   * @param {Number} reportId - The unique identifier of the report to delete.
   * @returns {Promise<void>} Resolves when the report is successfully deleted.
   */
  async deleteReport(reportId) {
    return await this.request.request(`health-safety/reports/${reportId}`, {
      method: "DELETE",
    });
  }

  /**
   * Fetches a specific health and safety report associated with a given manager by ID.
   *
   * @param {Number} reportId - The unique identifier of the report.
   * @returns {Promise<Object>} Resolves with the report data.
   */
  async getManagerReport(reportId) {
    return await this.request.request(`health-safety/manager/reports/${reportId}`);
  }

  /**
   * Fetches all health and safety reports associated with a given manager.
   *
   * @returns {Promise<Array>} Resolves with an array of reports.
   */
  async getManagerReports() {
    return await this.request.request(`health-safety/manager/reports`);
  }

  /**
   * Updates an existing health and safety report associated with a given manager.
   *
   * @param {Number} reportId - The unique identifier of the report.
   * @param {Object} data - The updated data for the report.
   * @returns {Promise<Object>} Resolves with the updated report data.
   */
  async patchManagerReport(reportId, data) {
    return await this.request.request(`health-safety/manager/reports/${reportId}`, {
      'method': 'PATCH',
      'data': data,
    });
  }

}

export default HealthSafety;
